import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
} from "@mui/material";
import isGHMC, { formatDayMonthYearTime, getCurrentDateTime, getCurrentDateTime1 } from "../../../Common/helper/helpar";
import { GetAllTertiaryDeploymentReport } from "../../../Apis/userCreationApi";
import Loader from "../../../Common/Loader/loader";
import ExcelJS from "exceljs";
import { getNowDateTime, getOneMonthPastDateTime } from "../../AssetVehicle/utils/helpers";
import TableSkeleton from "../../AllotedMaster/TableSkeleton";
const getStartOfCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00`;
};

const TertiaryDeploymentDetails = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [fromDate, setFromDate] = useState(getStartOfCurrentDate());
  const [page, setPage] = useState(0);
  const [toDate, setToDate] = useState(getCurrentDateTime());
  const [dataLoading, setDataLoading] = useState(true);
  const [VehicleNumber, setVehicleNumber] = useState("");
  const isProd = isGHMC();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 100));
    setPage(0);
  };

  const fetchData = async (filters = {}) => {
    const queryParams = new URLSearchParams(filters).toString();
    try {
      setDataLoading(true);
      const data = await GetAllTertiaryDeploymentReport(queryParams);

      // Check if data exists and update state
      if (data && data.length > 0) {
        setRows(data); // Store data in rows
        setFilteredRows(data); // Initially, filteredRows are the same as rows
        console.log(filteredRows, "filtered rows");
        setDataLoading(false);
      } else {
        setRows([]); // Clear rows if no data
        setFilteredRows([]); // Clear filteredRows
        setDataLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setDataLoading(false);
    }
  };

  useEffect(() => {
    const filters = {
      fromDate: fromDate || "",
      toDate: toDate || "",
      VehicleNumber: VehicleNumber || " "
    };
    fetchData(filters);
  }, []);

  // Filter rows based on date range
  const handleFilter = () => {
    const filters = {
      fromDate: fromDate || "",
      toDate: toDate || "",
      VehicleNumber: VehicleNumber || " "
    };

    fetchData(filters);
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    const options = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };
    return date.toLocaleString("en-GB", options).replace(",", "");
  };

  const downloadAsExcel = async () => {
    // Prepare data for Excel (filteredRows will be used here)
    const formattedData = filteredRows.map((row, index) => ({
      "S.No": row.sno || index + 1,
      "Vehicle Number": row.vehicleNumber ?? 'N/A',
      "Driver Name": row.driverName ?? 'N/A',
      "Vehicle Type": row.vehicleTypeName ?? 'N/A',
      "Owner Type": row.ownerTypeName ?? 'N/A',
      "Departure Station": row.departureTransferStationName ?? 'N/A',
      "Destination Station": row.destinationTransferStationName ?? 'N/A',
      "Remarks":row.shiftClosureRemarks ?? 'N/A',
      "Deployment Date&Time": row.deploymentDateTime ?? 'N/A',
      "Haulting": row.isHalting?? "N/A",
      "Latitude": row.latitude ?? 'N/A',
      "Longitude": row.longitude ?? 'N/A',
      "Address": row.address ?? 'N/A',
      "Is Closed": row.isClosed?? "N/A",
      "Is Forced Closed":row.isForcedClosed?? "N/A",
    }));
    console.log(formattedData, "formattedData")
  
    // Create a new workbook and add a worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Tertiary Trip Report");
  
    // Define styles
    const boldFont = { bold: true };
    
    // Add the generation time header
    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `Report Generated on: ${formattedDate}, ${formattedTime}`;
    
    worksheet.addRow(["", "", downloadDateTime, ""]);
    worksheet.mergeCells(1, 2, 1, 10);
    worksheet.getCell(1, 2).value = downloadDateTime;
    worksheet.getCell(1, 2).font = boldFont;
    worksheet.getCell(1, 2).alignment = { horizontal: "center" };
  
    // Add section header
    worksheet.addRow(["", "", `Tertiary Deployment Report (${formatDateForDisplay(fromDate)} to ${(toDate)}) (at ${getCurrentDateTime1()})`, ""]);
    worksheet.mergeCells(2, 2, 2, 10);
    worksheet.getCell(2, 2).value = `Tertiary Deployment Report (${formatDateForDisplay(fromDate)} to ${(toDate)}) (at ${getCurrentDateTime1()})`;
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 2).alignment = { horizontal: "center" };
  
    // Add the table header row
    const tableHeaderRow = worksheet.addRow([
      "S.No", "Vehicle Number", "Driver Name","Vehicle Type","Owner Type", "Departure Station", 
      "Destination Station", "Remarks", "Deployment Date&Time",
      "Haulting", "Latitude", "Longitude", "Address", "Is Closed", 
      "Is Forced Closed"
    ]);
  
    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = boldFont;
      cell.alignment = { horizontal: "left" };
      if (colNumber >= 1 && colNumber <= 18) {
        // Apply background color to header
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F6B1B1" }, // Light red background
        };
      }
    });
  
    // Add data rows
    formattedData.forEach((item, index) => {
      worksheet.addRow([
        item["S.No"], item["Vehicle Number"], item["Driver Name"],item["Vehicle Type"],item["Owner Type"], item["Departure Station"],
        item["Destination Station"], item["Remarks"], item["Deployment Date&Time"],
        item["Haulting"], item["Latitude"], item["Longitude"], item["Address"], item["Is Closed"],
        item["Is Forced Closed"]
      ]).eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber >= 1 && colNumber <= 18) {
          cell.alignment = { horizontal: "left", vertical: 'middle', wrapText: true };
        }
      });
    });
  
    // Set column widths dynamically based on content
    worksheet.columns = [
      { width: 5 }, { width: 15 }, { width: 20 }, { width: 20 },{ width: 20 }, { width: 20 },
      { width: 20 }, { width: 25 }, { width: 25 }, { width: 15 },
      { width: 15 }, { width: 15 }, { width: 100 }, { width: 20 },
      { width: 20 }
    ];
  
    // Save the workbook to a file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Tertiary_Deployment_Report.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const headerStyles = {
    textAlign: "center",
    border: "1px solid #ddd",
    borderRight: "none",
    padding: "10px",
    color: "#fff",
    backgroundColor: isProd ? "#0d5da5" : "#EC1117",
  };
  const cellStyles = {
    textAlign: "center",
    border: "1px solid #ddd",
    borderRight: "none",
    padding: "10px",
    whiteSpace: "normal"
  };



  return (
    <div>
      <div style={{ width: "100%" }}>
        <div className="content">
          <div className="page-content">
            <h4 class="text-start mb-3">Tertiary Deployment Report ({formatDateForDisplay(fromDate)} to {(toDate)}) (at {getCurrentDateTime1()})</h4>
            <div className="card">
              <div className="card-body p-3">
                <div className="row align-items-end">
                  <div className="col-md-10"></div>
                  <div className="row" style={{ textAlign: "left" }}>
                    <div className="col-md-3 ">
                      <label>Start Date</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ marginRight: "20px" }}
                        min={getOneMonthPastDateTime()}
                        max={getNowDateTime()}                      
                      />
                    </div>
                    <div className="col-md-3 ">
                      <label>End Date</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ marginRight: "20px" }}
                        min={fromDate}
                        max={getCurrentDateTime()}
                      />
                    </div>
                    <div className="col-md-3 ">
                      <label>Vehicle Number</label>
                      <input
                        type="text"
                        name="vehicleTypeCode"
                        style={FilterinputStyle}
                        placeholder="Vehicle Number"
                        value={VehicleNumber}
                        onChange={(e) => {
                          setVehicleNumber(e.target.value.replace(/\s+/g, ""));
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <button
                          variant="contained"
                          className={`btn mt-4 ${
                            isProd ? "search-ghmc" : "search-swm"
                          }`}
                          onClick={handleFilter}
                          sx={{ padding: "10px 20px" }}
                        >
                          Search
                        </button>

                        <button
                          className="btn btn-excel "
                          onClick={downloadAsExcel}
                        >
                          <i class="fa-solid fa-file-excel me-1 icon-container"></i>{" "}
                          Excel
                        </button>
                      </div>
                    </div>
                  </div>
                  {dataLoading ? (
                    <TableSkeleton />
                  ) : (
                    <Paper sx={{ width: "115%", overflow: "hidden" }}>
                      <TableContainer
                        component={Paper}
                        sx={{
                          width: "100%",
                          margin: "20px 0",
                          maxHeight: "500px", // Set a maximum height for the container
                          overflowY: "auto",
                          // Enable vertical scrollbar if needed
                          boxShadow: "0px 3px 6px rgba(0,0,0,0.1)",
                          border: "1px solid #ccc",
                        }}
                      >
                        <Table stickyHeader aria-label="sticky table">
                          <TableHead
                            sx={{
                              position: "sticky",
                              top: 0,
                              zIndex: 1, // Ensure header stays above other content
                              backgroundColor: isProd ? "#0d5da5" : "#EC1117",
                            }}
                          >
                            <TableRow sx={{ paddingTop: "20px" }}>
                              <TableCell sx={headerStyles}>S.No</TableCell>
                              <TableCell sx={headerStyles}>
                                Vehicle Number
                              </TableCell>
                              <TableCell sx={headerStyles}>
                                Driver Name
                              </TableCell>
                              <TableCell sx={headerStyles}>
                                Vehicle Type
                              </TableCell>
                              <TableCell sx={headerStyles}>
                               Owner Type
                              </TableCell>
                              <TableCell sx={headerStyles}>
                                Departure Station
                              </TableCell>
                              <TableCell sx={headerStyles}>
                                Destination Station
                              </TableCell>
                              <TableCell sx={headerStyles}>Remarks</TableCell>
                              <TableCell sx={headerStyles}>
                                Deployment Date&Time
                              </TableCell>
                              <TableCell sx={headerStyles}>Haulting</TableCell>
                              <TableCell sx={headerStyles}>Latitude</TableCell>
                              <TableCell sx={headerStyles}>Longitude</TableCell>
                              <TableCell style={{minWidth: 200}} sx={headerStyles}>
                                Address
                              </TableCell>
                              <TableCell sx={headerStyles}>Is Closed</TableCell>
                              <TableCell sx={headerStyles}>
                                Is Force Closed
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredRows.length > 0 ? (
                              filteredRows
                                .slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage
                                )
                                .map((row, index) => (
                                  <TableRow
                                    key={row.sno}
                                    sx={{
                                      backgroundColor: isProd
                                        ? index % 2 === 0
                                          ? "#8fc9ff"
                                          : "#aed8ff"
                                        : index % 2 === 0
                                        ? "#F8CCCD"
                                        : "#ffeded",
                                      "&:nth-of-type(odd)": {
                                        backgroundColor: isProd
                                          ? "#aed8ff"
                                          : "#ffeded",
                                      },
                                      "&:nth-of-type(even)": {
                                        backgroundColor: isProd
                                          ? "#8fc9ff"
                                          : "#F8CCCD",
                                      },
                                    }}
                                  >
                                    <TableCell sx={cellStyles}>
                                      {" "}
                                      {row.sno || index + 1}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.vehicleNumber || "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.driverName || "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.vehicleTypeName || "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.ownerTypeName || "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.departureTransferStationName ||
                                        "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.destinationTransferStationName ||
                                        "N/A"}
                                    </TableCell>
                                    <TableCell
                                      sx={{ ...cellStyles, minWidth: 200 }}
                                    >
                                      {row.shiftClosureRemarks || "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.deploymentDateTime
                                        ? formatDayMonthYearTime(
                                            row.deploymentDateTime
                                          )
                                        : "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.isHalting.toString()}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.latitude || "N/A"}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.longitude || "N/A"}
                                    </TableCell>
                                    <TableCell sx={{...cellStyles, minWidth: 300}}>
                                      {row.address}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.isClosed.toString()}
                                    </TableCell>
                                    <TableCell sx={cellStyles}>
                                      {row.isForcedClosed.toString()}
                                    </TableCell>
                                  </TableRow>
                                ))
                            ) : (
                              <TableRow>
                                <TableCell
                                  colSpan={20}
                                  sx={{ textAlign: "center" }}
                                >
                                  No data available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={filteredRows.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Paper>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterinputStyle = {
  height: "38px",
  borderRadius: "4px",
  border: "1px solid #ced4da",
  paddingLeft: "10px",
  width: "100%",
};

export default TertiaryDeploymentDetails;
